import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, filter, map, of, switchMap, timer } from 'rxjs';

import { environment } from '../../environments/environment';

export interface AppVersionInfo {
  version: string;
  buildTime: string;
}

/** Clave de `sessionStorage` donde se recuerdan las versiones ya anunciadas. */
const ANNOUNCED_VERSIONS_KEY = 'intranet.version.announced';

/**
 * Lecturas consecutivas con la misma versión remota que se exigen antes de
 * avisar al usuario. Evita falsos positivos por respuestas inconsistentes de
 * CloudFront (distintos POPs sirviendo `version.json` viejo y nuevo).
 */
const REQUIRED_STABLE_READS = 2;

/**
 * Servicio para detectar despliegues nuevos en entornos estáticos (S3 + CloudFront)
 * consultando `version.json` periódicamente.
 */
@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly http = inject(HttpClient);
  private pollingStarted = false;

  /**
   * Versiones por las que ya se avisó al usuario en esta pestaña. Se marca al
   * momento de notificar (no al descartar), de modo que la supresión no dependa
   * de que el diálogo invoque su callback de rechazo.
   */
  private readonly announcedVersions = this.readAnnouncedVersions();

  /** Última versión remota vista y cuántas lecturas consecutivas la confirman. */
  private candidateVersion: string | null = null;
  private candidateReads = 0;

  private readonly _currentVersion = signal<AppVersionInfo | null>(null);
  readonly currentVersion = this._currentVersion.asReadonly();

  private readonly _updateAvailable = signal<AppVersionInfo | null>(null);
  readonly updateAvailable = this._updateAvailable.asReadonly();

  /**
   * Inicia un polling contra `version.json` y marca `updateAvailable` cuando detecta
   * un cambio de versión confirmado por varias lecturas consecutivas.
   * @param intervalMs Periodo de consulta en milisegundos
   */
  startPolling(intervalMs = 30_000): void {
    if (environment.environmentName === 'Local') return;
    if (this.pollingStarted) return;
    this.pollingStarted = true;

    timer(0, intervalMs)
      .pipe(
        // Mientras haya un aviso pendiente no tiene sentido seguir comparando.
        filter(() => this._updateAvailable() === null),
        // Sin conexión la consulta solo produce ruido en consola; se omite.
        filter(() => this.isOnline()),
        switchMap(() => this.fetchVersion()),
        map((remote) => this.evaluateRemoteVersion(remote)),
        filter((update): update is AppVersionInfo => update !== null),
      )
      .subscribe((update) => {
        this.rememberAnnouncedVersion(update.version);
        this._updateAvailable.set(update);
      });
  }

  /**
   * Limpia la bandera del aviso pendiente. Es idempotente: la versión ya quedó
   * registrada como anunciada al notificarla, así que invocarla de más (PrimeNG
   * puede emitir el rechazo del diálogo más de una vez) no reactiva el aviso.
   */
  clearUpdateAvailable(): void {
    const pending = this._updateAvailable();
    if (!pending) return;

    this.rememberAnnouncedVersion(pending.version);
    this._updateAvailable.set(null);
  }

  /**
   * Decide si una respuesta remota representa una versión nueva que amerite aviso.
   * @param remote Contenido de `version.json`, o `null` si la consulta falló
   * @returns La versión nueva confirmada, o `null` si no hay nada que anunciar
   */
  private evaluateRemoteVersion(
    remote: AppVersionInfo | null,
  ): AppVersionInfo | null {
    // Respuesta inválida (404, HTML del fallback de la SPA, JSON incompleto):
    // no se toma como señal ni rompe la cuenta de lecturas estables.
    if (!this.isValidVersionInfo(remote)) return null;

    const local = this._currentVersion();

    // Primera lectura válida de la sesión: establece la línea base.
    if (!local) {
      this._currentVersion.set(remote);
      return null;
    }

    if (remote.version === local.version) {
      this.candidateVersion = null;
      this.candidateReads = 0;
      return null;
    }

    if (this.announcedVersions.has(remote.version)) return null;

    // Exigir lecturas consecutivas antes de molestar al usuario.
    if (this.candidateVersion !== remote.version) {
      this.candidateVersion = remote.version;
      this.candidateReads = 1;
      return null;
    }

    this.candidateReads += 1;
    if (this.candidateReads < REQUIRED_STABLE_READS) return null;

    this.candidateVersion = null;
    this.candidateReads = 0;
    return remote;
  }

  /**
   * Valida que la respuesta tenga la forma esperada y una versión utilizable.
   * @param value Respuesta cruda de `version.json`
   */
  private isValidVersionInfo(
    value: AppVersionInfo | null,
  ): value is AppVersionInfo {
    if (!value || typeof value !== 'object') return false;

    const version = (value as AppVersionInfo).version;
    return (
      typeof version === 'string' &&
      version.trim().length > 0 &&
      version !== 'unknown'
    );
  }

  /**
   * Indica si el navegador reporta conexión. `navigator.onLine === false` es
   * confiable para saber que *no* hay red; `true` no garantiza salida a internet,
   * por eso solo se usa para omitir consultas, nunca para validar la respuesta.
   */
  private isOnline(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  }

  private fetchVersion() {
    const cacheBust = Date.now();
    const url = `version.json?v=${cacheBust}`;

    return this.http.get<AppVersionInfo>(url).pipe(catchError(() => of(null)));
  }

  /**
   * Registra una versión como ya anunciada y la persiste en `sessionStorage`
   * para que una recarga parcial o una segunda instancia no vuelvan a avisar.
   * @param version Identificador de la versión anunciada
   */
  private rememberAnnouncedVersion(version: string): void {
    this.announcedVersions.add(version);

    try {
      sessionStorage.setItem(
        ANNOUNCED_VERSIONS_KEY,
        JSON.stringify([...this.announcedVersions]),
      );
    } catch {
      // sessionStorage puede no estar disponible (modo privado); la supresión
      // en memoria sigue funcionando.
    }
  }

  /**
   * Recupera las versiones ya anunciadas en esta pestaña.
   */
  private readAnnouncedVersions(): Set<string> {
    try {
      const raw = sessionStorage.getItem(ANNOUNCED_VERSIONS_KEY);
      if (!raw) return new Set<string>();

      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set<string>();

      return new Set(parsed.filter((v): v is string => typeof v === 'string'));
    } catch {
      return new Set<string>();
    }
  }
}
